"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import cloudflareLoader from "@/lib/utils/cdn-cloudflare-loader";
import { useProjectMediaGalleriesState } from "@/store/store";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Image from "next/image";
import useHorizontalScroll from "../useHorizontalScrollHook";
import { useInfiniteImage } from "../../_hooks/useInfiniteImage";
const SwiperModal = ({
  isOpen,
  onClose,
  initialSlideIndex,
  digitalProfile
}) => {
  const [swiper, setSwiper] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [swiperComponents, setSwiperComponents] = useState(null);
  const [swiperLoading, setSwiperLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentThumbsIndex = useRef(initialSlideIndex);
  const hasPushedHistory = useRef(false);
  const {
    projectMediaGalleries
  } = useProjectMediaGalleriesState();
  const {
    fetchNextPage
  } = useInfiniteImage(digitalProfile);

  // Handle browser back button for mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      // Only push history if we haven't already
      if (!hasPushedHistory.current) {
        const modalState = {
          modalOpen: true
        };
        window.history.pushState(modalState, "", window.location.href);
        hasPushedHistory.current = true;
      }

      // Handle popstate event (back button)
      const handlePopState = event => {
        if (isOpen) {
          onClose();
        }
      };
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
        // Reset the flag when modal closes
        hasPushedHistory.current = false;
      };
    } else {
      // Reset the flag when modal is not open
      hasPushedHistory.current = false;
    }
  }, [isOpen, isMobile, onClose]);

  // Additional effect to handle modal state changes
  useEffect(() => {
    if (!isOpen && hasPushedHistory.current && isMobile) {
      // If modal closes and we had pushed history, reset the flag
      hasPushedHistory.current = false;
    }
  }, [isOpen, isMobile]);
  const handleThumbSlideChange = async () => {
    // Store current thumbs position before fetching
    if (thumbsSwiper && !thumbsSwiper.destroyed) {
      currentThumbsIndex.current = thumbsSwiper.activeIndex;
    }
    if (!digitalProfile?.id) return;
    await fetchNextPage();

    // Update swipers after data fetch
    if (swiper && !swiper.destroyed) {
      swiper.update();
    }

    // Update thumbs and restore position after a brief delay to ensure DOM is updated
    if (thumbsSwiper && !thumbsSwiper.destroyed) {
      thumbsSwiper.update();
      // Use setTimeout to ensure the update happens after the DOM has been updated with new slides
      setTimeout(() => {
        if (thumbsSwiper && !thumbsSwiper.destroyed) {
          thumbsSwiper.slideTo(currentThumbsIndex.current, 0, false);
        }
      }, 0);
    }
  };
  const loadMoreRef = useHorizontalScroll(handleThumbSlideChange);
  // Dynamic Swiper loading
  const loadSwiper = useCallback(async () => {
    if (swiperComponents || swiperLoading) return;
    setSwiperLoading(true);
    try {
      const [{
        Swiper,
        SwiperSlide
      }, {
        FreeMode,
        Navigation,
        Thumbs,
        Zoom
      }] = await Promise.all([import("swiper/react"), import("swiper/modules")]);

      // Dynamically import CSS
      await Promise.all([import("swiper/css"), import("swiper/css/free-mode"), import("swiper/css/navigation"), import("swiper/css/thumbs"), import("swiper/css/zoom")]);
      setSwiperComponents({
        Swiper,
        SwiperSlide,
        FreeMode,
        Navigation,
        Thumbs,
        Zoom
      });
    } catch (error) {
      console.error("Failed to load Swiper:", error);
    } finally {
      setSwiperLoading(false);
    }
  }, [swiperComponents, swiperLoading]);

  // Load Swiper when modal opens
  useEffect(() => {
    if (isOpen) {
      loadSwiper();
      currentThumbsIndex.current = initialSlideIndex;
    }
  }, [isOpen, loadSwiper, initialSlideIndex]);

  // Handle keyboard events
  const handleKeyDown = useCallback(event_0 => {
    if (!isOpen) return;
    switch (event_0.key) {
      case "ArrowLeft":
        event_0.preventDefault();
        if (swiper && !swiper.destroyed) {
          swiper.slidePrev();
        }
        break;
      case "ArrowRight":
        event_0.preventDefault();
        if (swiper && !swiper.destroyed) {
          swiper.slideNext();
        }
        break;
      case "Escape":
        event_0.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  }, [isOpen, swiper, onClose]);
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, handleKeyDown]);
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);
  if (!isOpen || !projectMediaGalleries?.data?.length) {
    return null;
  }
  const handleSlideChange = () => {
    if (!digitalProfile?.id) return;
    const currentIndex = swiper?.activeIndex;
    const totalImages = projectMediaGalleries?.data?.length ?? 0;
    if (totalImages > 0 && currentIndex >= totalImages - 2) {
      fetchNextPage();
      swiper.update();
    }
  };

  // Loading state
  if (!swiperComponents) {
    return <div className="fixed z-[9990] top-0 left-0 w-full h-full flex flex-col justify-center items-center bg-[#000000d5] p-2">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        <p className="text-white mt-4">Loading gallery...</p>
      </div>;
  }
  const {
    Swiper: Swiper_0,
    SwiperSlide: SwiperSlide_0,
    FreeMode: FreeMode_0,
    Navigation: Navigation_0,
    Thumbs: Thumbs_0,
    Zoom: Zoom_0
  } = swiperComponents;
  return <div className="fixed z-[9990] top-0 left-0 w-full h-full flex flex-col justify-center items-center bg-[#000000d5] p-2">
      <Swiper_0 key={`swiper-${initialSlideIndex}`} onSwiper={setSwiper} onSlideChange={handleSlideChange} style={{
      "--swiper-navigation-color": "#fff",
      "--swiper-pagination-color": "#fff",
      "--swiper-navigation-size": isMobile ? "32px" : "44px"
    }} initialSlide={initialSlideIndex} loop={false} spaceBetween={10} navigation={true} zoom={isMobile ? {
      maxRatio: 3,
      minRatio: 1,
      toggle: true
    } : false} thumbs={{
      swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null
    }} modules={isMobile ? [FreeMode_0, Navigation_0, Thumbs_0, Zoom_0] : [FreeMode_0, Navigation_0, Thumbs_0]} className="max-h-[70vh] h-full w-full rounded-lg">
        {projectMediaGalleries?.data?.map((image, index) => <SwiperSlide_0 key={`swiper-${image?.id || index}`}>
            <div className="swiper-zoom-container h-full w-full flex items-center justify-center relative">
              <Image src={cloudflareLoader({
            src: image?.attributes?.media_url,
            quality: 95
          })} alt="Swiper Slides" className="w-full object-contain" fill sizes="100%" priority />
            </div>
          </SwiperSlide_0>)}
      </Swiper_0>

      {/* Thumbnail */}
      <Swiper_0 key={`thumbs-${initialSlideIndex}`} onSwiper={setThumbsSwiper} onSlideChange={swiper_0 => {
      currentThumbsIndex.current = swiper_0.activeIndex;
    }} initialSlide={initialSlideIndex} loop={false} spaceBetween={10} slidesPerView={5} freeMode={true} watchSlidesProgress={true} modules={[FreeMode_0, Navigation_0, Thumbs_0]} className="thumbs mt-3 w-full h-24 md:h-28 lg:h-32">
        {projectMediaGalleries?.data?.map((image_0, index_0) => <SwiperSlide_0 key={`thumb-${image_0?.id || index_0}`}>
            <button className="flex w-full h-full items-center justify-center" ref={projectMediaGalleries?.data?.length - 1 === index_0 ? loadMoreRef : null}>
              <Image
          // loader={cloudflareLoader}
          // src={image?.attributes?.media_url}
          src={cloudflareLoader({
            src: image_0?.attributes?.media_url,
            quality: 95
          })} alt="Swiper Thumbnail" className="block h-full w-full object-cover" fill sizes="100%" />
            </button>
          </SwiperSlide_0>)}
      </Swiper_0>
      <button className="fixed top-6 right-6 z-50 md:scale-150" onClick={handleClose}>
        <CloseIcon color="white" className="text-white" />
      </button>
    </div>;
};
export default SwiperModal;